import React, { createContext, useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getOptions, insertOptions, getOptionsByKey } from "../services/defaultValuesService";
import { setRsplListFromOptions } from "../config";
import i18n from "../i18n";
// Create the context
const OptionsContext = createContext();

// Custom hook to use the context
export const useOptions = () => useContext(OptionsContext);

const OptionsProvider = ({ children }) => {
	const navigate = useNavigate();
	const [options, setOptions] = useState(null);
	const [optionKeys, setOptionKeys] = useState(null);
	const [selectedTheme, setSelectedTheme] = useState(null);
	const [mfaDays, setMfaDays] = useState(null);
	const [mfaEnabled, setMfaEnabled] = useState(null);
	const [optionsLoading, setOptionsLoading] = useState(true);
	const [optionsLoaded, setOptionsLoaded] = useState(false);
	const [optionsError, setOptionsError] = useState(null);
	const supportedLanguages = ["en", "fr", "es", "de"];

	const convertBoolean = (value) => value === "true";

	/**
	 * Load every option row into context.
	 *
	 * Reports outcome through `optionsLoaded` / `optionsError` as well as
	 * `optionsLoading`, because "finished loading" and "actually has the stored
	 * values" used to be the same flag: a failed fetch was swallowed here and
	 * `optionsLoading` still went false, so Settings rendered its blank initial
	 * defaults as if they were the saved configuration and the next Save wrote
	 * those blanks over real production data. Consumers must gate on
	 * `optionsLoaded`, not on `!optionsLoading`.
	 *
	 * @returns {Promise<void>} Resolves once the request settles; never rejects.
	 */
	const fetchOptions = async () => {

		setOptionsLoading(true);
		setOptionsError(null);
		try {
			const response = await getOptions();


			if (response.status) {
			const opts = response.data.options; // this is an object
			setOptionKeys(opts); // save the whole object
			setOptions(response.data);
			setRsplListFromOptions(opts?.rsplList);

			// Access properties directly
			setMfaDays(parseInt(opts.mfaDays) || 15);
			setMfaEnabled(opts.mfaEnabled === "Enabled"); // boolean
			setSelectedTheme(opts.accentColor || opts.theme || "blue");
			setOptionsLoaded(true);
			} else {
			setOptionsLoaded(false);
			setOptionsError(response?.message || "Failed to load settings");
			}
		} catch (error) {
			setOptionsLoaded(false);
			setOptionsError(error?.message || "Failed to load settings");
		} finally {
			setOptionsLoading(false);
		}
		};

	const setOption = async (options) => {
		try {
			const resp = await insertOptions({options});
			if (resp.status) {
				
			}
		} catch (error) {
			
		}
	};

	const getOption = async (option_key) => {
		try {
			const resp = await getOptionsByKey(option_key);
			if (resp.status) {
				
			}
		} catch (error) {
			
		}
	};

	useEffect(() => {
		fetchOptions();
	}, []);

	useEffect(() => {
		if (!optionKeys) return;
		const translationEnabled = optionKeys.enableLanguageTranslation !== "false";
		const fallbackLanguage = supportedLanguages.includes(optionKeys.defaultLanguage)
			? optionKeys.defaultLanguage
			: "en";
		if (!translationEnabled) {
			if (i18n.language !== fallbackLanguage) {
				i18n.changeLanguage(fallbackLanguage);
			}
			return;
		}
		if (!supportedLanguages.includes(i18n.language)) {
			i18n.changeLanguage(fallbackLanguage);
		}
	}, [optionKeys]);

	return (
		<OptionsContext.Provider
			value={{
				options,
				optionKeys,
				optionsLoading,
				optionsLoaded,
				optionsError,
				selectedTheme,
				mfaDays,
				mfaEnabled,
				setOption,
				getOption,
				fetchOptions
			}}>
			{children}
		</OptionsContext.Provider>
	);
};

export default OptionsProvider;
